import ReactSelect from 'react-select';
import reactSelectStylesConfig from 'lib/react-select';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { fetchExams } from '../../../api/exams';
import { fetchStudents } from '../../../api/students';
import { fetchGroups } from '../../../api/groups';
import { fetchAttempts } from '../../../api/analytics';
import { reactQueryConfig } from '../../../lib/react-query';
import { useAuthStore } from 'store/auth';
import { maskValue } from 'utils/mask';
import { ThreeDots } from 'react-loader-spinner'; 

interface Props {
  payload: { examID: string; userIDs: string[]; attempts: string[];};
  setPayload: React.Dispatch<React.SetStateAction<{ examID: string; userIDs: string[]; attempts: string[] }>>;
}

export const Filters = ({ payload, setPayload }: Props) => {
  const { organizationId } = useParams();
  const { user } = useAuthStore()
  const [groupId, setGroupId] = useState('');
  const [selectState, setSelectState] = useState<{ label: string; value: string }[] | null>();
  const [selectAttemptState, setSelectAttemptState] = useState<
    { label: string; value: string }[] | [{ label: 'All'; value: 'all' }]
  >([{ label: 'All', value: 'all' }]);

  const { data: exams, isLoading: isExamsLoading } = useQuery(['org-exams'], () => fetchExams());

  const { data: students, refetch: refetchStudents } = useQuery(
    ['org-students'],
    () => fetchStudents({ filter: { group: groupId, all: true } }),
    { enabled: Boolean(groupId.length) }
  );

  const { data: groups } = useQuery(['org-groups'], () => fetchGroups({ organizationId }), {
    enabled: Boolean(organizationId),
  });

  const { data: attempts } = useQuery(['org-attempts'], () => fetchAttempts(payload), {
    enabled: payload.userIDs.length === 1 && payload.userIDs.some(({ value }: any) => value === 'all') === false,
  });

  const isAllStudentsSelected = payload.userIDs.length === students?.result?.length;
  const isAllAttemptsSelected = payload.attempts.length === attempts?.length;

  useEffect(() => {
    if (exams?.result?.length && !payload.examID) {
      setPayload((prev) => ({ ...prev, examID: exams.result[0]._id }));
    }
  }, [exams, payload.examID, setPayload]);

  useEffect(() => {
    if (groups?.result?.length && !groupId) {
      setGroupId(groups.result[0]._id);
    }
  }, [groups, groupId]);

  useEffect(() => {
    if (Boolean(groupId.length)) refetchStudents();
  }, [groupId,refetchStudents]);
  if (isExamsLoading || !exams) {
    return (
      <div className="flex justify-center my-10">
        <ThreeDots height="80" width="80" radius="9" color="#87C6E8" ariaLabel="three-dots-loading" visible={true} />
      </div>
    );
  }

  if (exams.result.length === 0) {
    return (
      <div className="p-4 mb-4 text-sm text-gray-500 bg-gray-50 rounded-md border border-gray-200">
        No exams available. Please create an exam to view analytics.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mb-4">
      {exams?.result && exams.result.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-500 mr-2">Exams:</span>
          {exams.result.map(({ _id, name }) => {
            const isSelected = payload.examID === _id;
            return (
              <button
                key={_id}
                type="button"
                onClick={() => setPayload({ ...payload, examID: _id })}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}
      {groups?.result && groups.result.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-500 mr-2">Groups:</span>
          {groups.result.map(({ _id, name }) => {
            const isSelected = groupId === _id;
            const maskedName = user?.isCensorshipEnabled ? maskValue(name) : name;
            return (
              <button
                key={_id}
                type="button"
                onClick={() => {
                  if (isSelected) return; 
                  setGroupId(_id);
                  setSelectState(null);
                  setSelectAttemptState([{ label: 'All', value: 'all' }]);
                  setPayload({ ...payload, userIDs: [], attempts: [] });
                  reactQueryConfig.removeQueries(['students-analytics']);
                }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {maskedName}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3">
        {groupId ? (
          <div className="min-w-[250px]">
            <ReactSelect
              isMulti
              styles={reactSelectStylesConfig}
              value={selectState}
              options={
                isAllStudentsSelected
                  ? [{ label: 'All', value: 'all' }]
                  : [
                      { label: 'All', value: 'all' },
                      ...(students?.result.map(({ _id, name }, index) => {
                        const maskedName = user?.isCensorshipEnabled ? maskValue(name) : name;
                        return { label: `${index + 1} ${maskedName}`, value: _id };
                      }) || []),
                    ]
              }
              onChange={(val: any) => {
                if (val.some(({ value }: any) => value === 'all')) {
                  setSelectState([{ label: 'All', value: 'all' }]);
                  setPayload({ ...payload, userIDs: students?.result?.map(({ _id }) => _id) || [] });
                  return;
                }
                setSelectState(val);
                setPayload({ ...payload, userIDs: val.map(({ value }: any) => value) });
              }}
              placeholder="Select Students"
            />
          </div>
        ) : null}

        {payload.userIDs.length === 1 && selectState?.some(({ value }: any) => value === 'all') === false && (
          <div className="min-w-[250px]">
            <ReactSelect
              isMulti
              styles={reactSelectStylesConfig}
              value={selectAttemptState}
              options={
                isAllAttemptsSelected
                  ? [{ label: 'All', value: 'all' }]
                  : [
                      { label: 'All', value: 'all' },
                      ...(attempts?.map(({ _id }: any, index) => ({
                        label: 'Attempt ' + (index + 1),
                        value: _id,
                      })) || []),
                    ]
              }
              onChange={(val: any) => {
                if (val.some(({ value }: any) => value === 'all')) {
                  setSelectAttemptState([{ label: 'All', value: 'all' }]);
                  setPayload({ ...payload, attempts: attempts?.map(({ _id }: any) => _id) || [] });
                  return;
                }
                setSelectAttemptState(val);
                setPayload({ ...payload, attempts: val.map(({ value }: any) => value) });
              }}
              placeholder="Select attempts"
            />
          </div>
        )}
      </div>
    </div>
  );
};
